/**
 * Global JS error hooks.
 *
 * Two signals to capture in the browser:
 *   * `window.error` — synchronous throws from event handlers, parser
 *     errors, etc. Receives `(message, source, lineno, colno, error)`.
 *   * `window.unhandledrejection` — promise rejections that escape every
 *     handler. The original error is on `event.reason`.
 *
 * Both are mirrored to /telemetry/crashes (treated as a crash by the
 * admin App Health page since they cause user-visible failures).
 *
 * NOTE: React render errors do NOT trip these hooks — they're caught by
 * React's reconciler and require an ErrorBoundary. See
 * components/ErrorBoundary.tsx.
 */

import { postCrash } from './api'

let installed = false

export function installErrorHooks(): void {
  if (installed) return
  installed = true

  window.addEventListener('error', (event) => {
    const e = event.error
    const exception = (e?.message ?? String(event.message ?? 'unknown error')).toString()
    const stackTrace = (e?.stack ?? `${event.filename ?? ''}:${event.lineno ?? '?'}:${event.colno ?? '?'}`).toString()
    void postCrash({
      exception,
      stack_trace: stackTrace,
      route: currentRoute(),
    })
  })

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason
    let exception: string
    let stackTrace: string
    if (reason instanceof Error) {
      exception = reason.message
      stackTrace = reason.stack ?? `at <unhandledrejection> (no stack)`
    } else {
      exception = `Unhandled promise rejection: ${String(reason)}`
      stackTrace = `at <unhandledrejection> (non-Error reason; type ${typeof reason})`
    }
    void postCrash({
      exception,
      stack_trace: stackTrace,
      route: currentRoute(),
    })
  })
}

/** Used by ErrorBoundary and api/* to send the route the user was on. */
export function currentRoute(): string {
  return window.location.pathname + window.location.search
}
